/*
 * Copyright (c) 2022. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import { Chip, type ChipProps, Link as MuiLink, Typography } from '@mui/material';
import { type CSSProperties, Fragment, type ReactNode } from 'react';
import { overrideSx, type ThemeColors } from '../styles';

interface WrapTextProps extends CSSProperties {
  children: ReactNode;
}

export const WrapText: React.FC<WrapTextProps> = ({ children, ...rest }) => (
  <span style={{ whiteSpace: 'initial', lineHeight: 'initial', ...rest }}>{children}</span>
);

// biome-ignore lint/style/useComponentExportOnlyModules: this is ok
export const insertLineBreaks = (s: string | undefined | null) => {
  if (!s) return [];
  let lineOffset = 0;
  return s.split('\r\n').map((line) => {
    const key = `${lineOffset}-${line.slice(0, 10)}`;
    lineOffset += line.length + 2;

    return (
      <Fragment key={key}>
        {line}
        <br />
      </Fragment>
    );
  });
};

interface InfoPillProps extends Omit<ChipProps, 'onClick' | 'color'> {
  height?: number;
  width?: number;
  color?: string | ThemeColors;
}

export const InfoPill = ({ height, width, color, ...rest }: InfoPillProps) => {
  return (
    <Chip
      {...rest}
      variant="outlined"
      sx={(t) => {
        let colorStr = typeof color === 'object' ? color[t.palette.mode] : color;
        if (
          colorStr &&
          ['primary', 'secondary', 'info', 'success', 'warning', 'error'].includes(colorStr)
        ) {
          colorStr = `${colorStr}.main`;
        }
        return overrideSx(
          {
            height: height ?? 20,
            width,
            borderRadius: 2,
            pt: 0.3,
            pb: 0.2,
            color: colorStr,
            borderColor: colorStr,
          },
          t,
          rest.sx,
        );
      }}
    />
  );
};

const linkLabel = (link: string) => {
  let service: string | undefined;
  if (link.includes('/pvsim')) {
    service = 'PVSim';
  } else if (link.includes('/ess')) {
    service = 'ESS';
  } else if (link.includes('/plant')) {
    service = 'PlantSim';
  } else if (link.includes('/fimo')) {
    service = 'FIMO';
  }

  if (!service) {
    return 'PowerAlpha';
  }

  if (link.includes('config')) {
    return `${service} Config`;
  }

  if (link.includes('scenario')) {
    return `${service} Scenario`;
  }

  return service;
};

interface RenderAppLinkProps {
  text: string | null | undefined;
}

export const RenderAppLink = ({ text }: Readonly<RenderAppLinkProps>) => {
  // Regular expression to detect URLs
  const urlRegex = /(https?:\/\/(?:www\.)?poweralpha\.com[^\s]*)/g;

  // Split text into parts (URLs and non-URLs)
  const parts = text?.split(urlRegex);

  if (!parts) {
    return null;
  }

  return (
    <Typography
      sx={{
        fontSize: 14,
      }}
    >
      {parts.map((part) => {
        // Check if the current part is a URL
        if (urlRegex.test(part)) {
          return (
            <MuiLink key={part} href={part} target="_blank" rel="noopener noreferrer">
              {linkLabel(part)}
            </MuiLink>
          );
        }
        // Render plain text if not a URL
        return <span key={part}>{part}</span>;
      })}
    </Typography>
  );
};

export const Superscript = ({ children }: { children: string }) => (
  <span style={{ fontSize: 8, verticalAlign: 'super' }}>{children}</span>
);
